/**
 * Diff 审查操作栏
 *
 * 在 diff 模式下替代普通工具栏，显示接受/拒绝按钮。
 */

import React, { useMemo } from 'react';

// ============================================
// Props
// ============================================

export interface DiffReviewToolbarProps {
  /** 原始代码行数 */
  readonly originalLineCount: number;
  /** 新代码行数 */
  readonly incomingLineCount: number;
  /** 代码语言名称 */
  readonly languageLabel: string;
  /** 接受回调 */
  readonly onAccept: () => void;
  /** 拒绝回调 */
  readonly onReject: () => void;
}

// ============================================
// 组件
// ============================================

export const DiffReviewToolbar: React.FC<DiffReviewToolbarProps> = ({
  originalLineCount,
  incomingLineCount,
  languageLabel,
  onAccept,
  onReject
}) => {
  const lineDelta = incomingLineCount - originalLineCount;

  const deltaLabel = useMemo((): string => {
    if (lineDelta > 0) return `+${lineDelta} 行`;
    if (lineDelta < 0) return `${lineDelta} 行`;
    return '行数不变';
  }, [lineDelta]);

  const deltaClass = useMemo((): string => {
    if (lineDelta > 0) return 'plc-diff-delta-add';
    if (lineDelta < 0) return 'plc-diff-delta-remove';
    return 'plc-diff-delta-same';
  }, [lineDelta]);

  return (
    <div className="plc-diff-toolbar">
      {/* 左侧：信息 */}
      <div className="plc-diff-toolbar-info">
        <i className="fa fa-code-branch plc-diff-toolbar-icon" />
        <span className="plc-diff-toolbar-title">代码审查</span>
        <span className="plc-diff-toolbar-sep">|</span>
        <span className="plc-diff-toolbar-lang">{languageLabel}</span>
        <span className={`plc-diff-toolbar-delta ${deltaClass}`}>
          {deltaLabel}
        </span>
      </div>

      {/* 右侧：操作 */}
      <div className="plc-diff-toolbar-actions">
        <button
          type="button"
          className="plc-diff-btn plc-diff-btn-reject"
          onClick={onReject}
          title="拒绝变更，保留原代码"
        >
          <i className="fa fa-times" />
          <span>拒绝</span>
        </button>
        <button
          type="button"
          className="plc-diff-btn plc-diff-btn-accept"
          onClick={onAccept}
          title="接受变更，应用新代码"
        >
          <i className="fa fa-check" />
          <span>接受</span>
        </button>
      </div>
    </div>
  );
};

DiffReviewToolbar.displayName = 'DiffReviewToolbar';

export default DiffReviewToolbar;